import { Button, Col, Divider, Row, Typography } from 'antd';
import { H4, TextSmall, TextSmallBold } from 'components/ui/base/typography';
import useDeclarationFormErrors from 'hooks/useDeclarationFormErrors';
import { FC, Fragment } from 'react';
import { ParsedCard, ParsedField, ParsedHouseConsignment, ParsedItem } from 'store/declaration-form-errors/ParsedForm';
import useErrorFieldLink from 'views/declarations/common/errors/useErrorFieldLink';
import { ErrorFieldLink } from 'views/declarations/common/errors/types';
import { CardNameTitle, CustomModal, ProductTitle } from './ValidationErrorContainer.styles';

interface Props {
    isOpen: boolean;
    close: Function;
    onNavigate?: () => void;
}

/**
 * A field message is normally the text of the validator that failed, but a validator that failed on a
 * nested block reports that block's own errors instead - an object, or an array of them. Those are
 * not messages, and React throws on being handed one as a child, so only text survives.
 */
const messageText = (message: ParsedField['message']): string | undefined => {
    if (typeof message === 'string') return message || undefined;
    if (typeof message === 'number' || typeof message === 'boolean') return String(message);

    if (Array.isArray(message)) {
        const texts = message.filter((entry): entry is string => typeof entry === 'string' && entry !== '');

        return texts.length ? texts.join(', ') : undefined;
    }

    return undefined;
};

/**
 * A level with no entries of its own is reported as a single entry numbered 0 and holding nothing,
 * which marks the tab but has no fields to list here.
 */
const hasErrors = ({ errors, items }: ParsedItem & Pick<ParsedHouseConsignment, 'items'>) =>
    Boolean(errors?.length || items?.length);

const ErrorCards: FC<{ cards?: ParsedCard[]; link?: ErrorFieldLink }> = ({ cards, link }) => (
    <>
        {cards?.map((card, cardIndex) => (
            <Fragment key={`${card.name}_${cardIndex}`}>
                <CardNameTitle>{card.name}</CardNameTitle>
                <Row gutter={[16, 16]}>
                    {card.fields
                        ?.map((field) => ({
                            name: field.name,
                            text: messageText(field.message),
                            onClick: link?.(field),
                        }))
                        .filter(({ text }) => text !== undefined)
                        .map(({ name, text, onClick }, fieldIndex) => (
                            <Fragment key={`${name}_${fieldIndex}`}>
                                <Col span={8}>
                                    {/* Only a field we can find in the form is worth offering as a link. */}
                                    {onClick ? (
                                        <Typography.Link onClick={onClick}>
                                            <TextSmall>{name}:</TextSmall>
                                        </Typography.Link>
                                    ) : (
                                        <TextSmallBold>{name}:</TextSmallBold>
                                    )}
                                </Col>
                                <Col span={16}>
                                    <TextSmall>{text}</TextSmall>
                                </Col>
                            </Fragment>
                        ))}
                </Row>
                <Divider />
            </Fragment>
        ))}
    </>
);

const ErrorItems: FC<{
    items?: ParsedItem[];
    title: (index: number) => string;
    link?: (item: ParsedItem) => ErrorFieldLink | undefined;
}> = ({ items, title, link }) => (
    <>
        {items?.filter(hasErrors).map((item) => (
            <Fragment key={title(item.index)}>
                <ProductTitle>{title(item.index)}</ProductTitle>
                <ErrorCards cards={item.errors} link={link?.(item)} />
            </Fragment>
        ))}
    </>
);

const ValidationErrorContainer: FC<Props> = ({ isOpen, close, onNavigate }) => {
    const { declarationErrors } = useDeclarationFormErrors();

    const linkTo = useErrorFieldLink(() => {
        close();
        onNavigate?.();
    });

    return (
        <CustomModal
            title={
                <>
                    <H4>Form Errors</H4>
                </>
            }
            centered
            closable={true}
            visible={isOpen}
            onCancel={() => {
                close();
            }}
            width={800}
            cancelText="Cancel"
            zIndex={1000}
            footer={[
                <Button
                    key="goToSettings"
                    onClick={() => {
                        close();
                    }}
                    size="large"
                >
                    Close
                </Button>,
            ]}
        >
            <ErrorCards cards={declarationErrors.masterDetails} link={linkTo('master')} />

            <ErrorItems
                items={declarationErrors.items}
                title={(index) => `Product ${index}`}
                link={(item) => linkTo('item', { item })}
            />

            {/* ICS2 reports two more levels of its own, each with goods items of its own. */}
            <ErrorItems
                items={declarationErrors.masterItems}
                title={(index) => `Master Item ${index}`}
                link={(item) => linkTo('masterItem', { item })}
            />

            {declarationErrors.houseConsignments?.filter(hasErrors).map((houseConsignment) => (
                <Fragment key={`house-consignment-${houseConsignment.index}`}>
                    <ProductTitle>{`House Consignment ${houseConsignment.index}`}</ProductTitle>
                    <ErrorCards
                        cards={houseConsignment.errors}
                        link={linkTo('houseConsignment', { houseConsignment })}
                    />
                    <ErrorItems
                        items={houseConsignment.items}
                        title={(index) => `House Consignment ${houseConsignment.index} - Item ${index}`}
                        link={(item) => linkTo('houseItem', { houseConsignment, item })}
                    />
                </Fragment>
            ))}
        </CustomModal>
    );
};

export default ValidationErrorContainer;
